import React, { useEffect, useState } from "react";
import PaginatedTable from "../common/PaginatedTable";
import DTIPages from "../common/DTIPages";
import DtiToolbar from "../common/DtiToolbar";
import DtiKpiGrid from "../common/DtiKpiGrid";
import DtiSectionHeading from "../common/DtiSectionHeading";
import DtiOverviewSkeleton from "../common/DtiSkeleton";
import { Tabs } from "@instructure/ui-tabs";
import { downloadWithAuth } from "../../../utils";
import {
  getJobAssessmentTracking,
  getAssessmentTracking,
  createJobAssessmentTracking,
  exportAssessmentTracking,
} from "../api/api";

const STAT_HEADERS_SUBJECT = [
  { id: "subject", text: "Môn học", width: "30%" },
  { id: "total_courses", text: "Số lớp", width: "14%" },
  { id: "total_quizzes", text: "Bài trắc nghiệm", width: "14%" },
  { id: "total_assignments", text: "Bài tập", width: "21%" },
  { id: "total_tests", text: "Tổng bài kiểm tra", width: "21%" },
];

const STAT_HEADERS_GRADE = [
  { id: "grade", text: "Khối", width: "20%" },
  { id: "total_courses", text: "Số lớp", width: "20%" },
  { id: "total_quizzes", text: "Bài trắc nghiệm", width: "20%" },
  { id: "total_assignments", text: "Bài tập", width: "20%" },
  { id: "total_tests", text: "Tổng bài kiểm tra", width: "20%" },
];

const STAT_HEADERS_GRADE_SUBJECT = [
  { id: "grade", text: "Khối", width: "12%" },
  { id: "subject", text: "Môn học", width: "28%" },
  { id: "total_courses", text: "Số lớp", width: "15%" },
  { id: "total_quizzes", text: "Bài trắc nghiệm", width: "15%" },
  { id: "total_assignments", text: "Bài tập", width: "15%" },
  { id: "total_tests", text: "Tổng bài kiểm tra", width: "15%" },
];

export default function AssessmentTracking({ iframe = false }) {
  const [isFetching, setIsFetching] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [dataRows, setDataRows] = useState({});
  const [kpi, setKpi] = useState({ totalTest: null, totalInteractiveTest: null });
  const [statsTab, setStatsTab] = useState(0);

  const loadAssessmentData = async () => {
    const dataAssessmentTracking = await getAssessmentTracking();
    setKpi({
      totalTest: dataAssessmentTracking.totalTest,
      totalInteractiveTest: dataAssessmentTracking.totalInteractiveTest,
    });
    setDataRows(dataAssessmentTracking);
  };

  useEffect(() => {
    let intervalId;
    const fetchData = async () => {
      setIsFetching(true);
      try {
        await loadAssessmentData();
        const job = await getJobAssessmentTracking();
        if (["starting", "pending"].includes(job?.workflowState)) {
          setIsLoading(true);
          intervalId = setInterval(async () => {
            const jobStatus = await getJobAssessmentTracking();
            if (jobStatus?.workflowState === "done") {
              setIsLoading(false);
              clearInterval(intervalId);
              setIsFetching(true);
              try {
                await loadAssessmentData();
              } finally {
                setIsFetching(false);
              }
            }
          }, 1000);
        }
      } finally {
        setIsFetching(false);
      }
    };
    fetchData();
    return () => clearInterval(intervalId);
  }, []);

  const handleJob = async () => {
    if (isLoading) return;
    setIsLoading(true);
    await createJobAssessmentTracking();
    const intervalId = setInterval(async () => {
      const job = await getJobAssessmentTracking();
      if (job?.workflowState === "done") {
        setIsLoading(false);
        clearInterval(intervalId);
        setIsFetching(true);
        try {
          await loadAssessmentData();
        } finally {
          setIsFetching(false);
        }
      }
    }, 1000);
  };

  const showSkeleton = isFetching || isLoading;

  const handleExport = async (event) => {
    event.preventDefault();
    if (isExporting) return;
    setIsExporting(true);
    try {
      await downloadWithAuth(
        exportAssessmentTracking(),
        "6.6-dgtx-quiz-assignment.xlsx",
      );
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(
        "[LỖI] Xuất báo cáo ĐGTX thất bại:",
        error && error.message ? error.message : error,
      );
    } finally {
      setIsExporting(false);
    }
  };

  const assignmentRows = dataRows?.dataTest?.dataAssignment || [];
  const quizRows = dataRows?.dataTest?.dataQuiz || [];
  const testRows = assignmentRows.concat(quizRows);

  if (iframe) {
    return (
      <PaginatedTable
        headers={STAT_HEADERS_SUBJECT}
        rows={dataRows?.data?.summaryBySubject || []}
        perPage={15}
      />
    );
  }

  return (
    <>
      {showSkeleton ? (
        <DtiOverviewSkeleton kpiCount={2} showExport />
      ) : (
        <>
      <DtiToolbar
        isLoading={isLoading}
        onRefresh={handleJob}
        onExport={handleExport}
        isExporting={isExporting}
        exportLabel="Tải báo cáo Excel"
        hint="Số liệu bài kiểm tra trực tuyến và học sinh tham gia làm bài ĐGTX."
      />

      <DTIPages
        firstPage={
          <DtiKpiGrid
            items={[
              { id: "totalTest", label: "Bài đánh giá thường xuyên đã tạo", value: kpi.totalTest },
              {
                id: "totalInteractiveTest",
                label: "Học sinh tham gia làm bài ĐGTX",
                value: kpi.totalInteractiveTest,
              },
            ]}
          />
        }
        secondPage={
          <>
            <DtiSectionHeading>Danh sách bài đánh giá thường xuyên</DtiSectionHeading>
            <PaginatedTable
              headers={[
                { id: "course_name", text: "Tên khóa học", width: "20%" },
                { id: "term", text: "Niên khóa", width: "20%" },
                { id: "title", text: "Tiêu đề bài ĐGTX", width: "20%" },
                { id: "created_at", text: "Ngày tạo", width: "20%" },
                { id: "updated_at", text: "Chỉnh sửa gần nhất", width: "20%" },
              ]}
              rows={testRows}
              perPage={10}
            />

            <DtiSectionHeading>Danh sách học sinh tham gia làm bài</DtiSectionHeading>
            <PaginatedTable
              headers={[
                { id: "user_id", text: "Mã học sinh", width: "40%" },
                { id: "name", text: "Họ và tên", width: "60%" },
              ]}
              rows={dataRows?.dataInteractiveTest || []}
              perPage={10}
            />

            <div className="dti-tabs-wrap">
              <DtiSectionHeading>Thống kê theo môn, khối</DtiSectionHeading>
              <Tabs onRequestTabChange={(e, { index }) => setStatsTab(index)}>
                <Tabs.Panel
                  id="stats-subject"
                  renderTitle="Theo môn học"
                  isSelected={statsTab === 0}
                >
                  <PaginatedTable
                    headers={STAT_HEADERS_SUBJECT}
                    rows={dataRows?.data?.summaryBySubject || []}
                    perPage={10}
                  />
                </Tabs.Panel>
                <Tabs.Panel
                  id="stats-grade"
                  renderTitle="Theo khối"
                  isSelected={statsTab === 1}
                >
                  <PaginatedTable
                    headers={STAT_HEADERS_GRADE}
                    rows={dataRows?.data?.summaryByGrade || []}
                    perPage={10}
                  />
                </Tabs.Panel>
                <Tabs.Panel
                  id="stats-grade-subject"
                  renderTitle="Theo khối & môn"
                  isSelected={statsTab === 2}
                >
                  <PaginatedTable
                    headers={STAT_HEADERS_GRADE_SUBJECT}
                    rows={dataRows?.data?.summaryByGradeSubject || []}
                    perPage={10}
                  />
                </Tabs.Panel>
              </Tabs>
            </div>
          </>
        }
      />
        </>
      )}
    </>
  );
}
